import { useState, useEffect, useRef } from 'react';
import {
  Alert,
  AlertTitle,
  Button,
  Box,
  Slide,
  IconButton,
  CircularProgress,
  Typography,
  Card,
  CardContent,
  CardActions,
  Fade,
  useTheme,
} from '@mui/material';
import {
  GetApp as InstallIcon,
  Refresh as UpdateIcon,
  Close as CloseIcon,
  CheckCircle as CheckIcon,
  PhoneAndroid as PhoneIcon,
} from '@mui/icons-material';
import { useVersionNotifier, useTranslation } from '@/hooks';
import { storageService } from '@/services/storageService';

export interface VersionNotifierProps {
  /**
   * Position of the notification
   * @default 'bottom'
   */
  position?: 'top' | 'bottom';

  /**
   * Auto-hide duration in milliseconds
   * Set to 0 to disable auto-hide
   * @default 8000
   */
  autoHideDuration?: number;

  /**
   * Enable/disable the notification
   * @default true
   */
  enabled?: boolean;

  /**
   * Force show install notification for testing
   * @default false
   */
  forceShowInstall?: boolean;
}
//  Enhanced VersionNotifier component with improved state management and styling
export function VersionNotifier({
  position: _position = 'bottom',
  autoHideDuration: _autoHideDuration = 8000,
  enabled = true,
}: VersionNotifierProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    hasUpdate,
    isUpdating,
    isVersionReady,
    canInstall,
    isInstalling,
    isInstalled,
    isReady,
    reloadForUpdate,
    installApp,
    dismissUpdate,
    reloadApp,
  } = useVersionNotifier();

  // Enhanced notification states with install toaster
  type NotificationState =
    | 'none'
    | 'update'
    | 'install'
    | 'installed'
    | 'version-ready';
  const [notificationState, setNotificationState] =
    useState<NotificationState>('none');
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const [
    hasShownSessionInstalledNotification,
    setHasShownSessionInstalledNotification,
  ] = useState(false);

  // Control visibility based on states with improved logic and 15-second install toaster
  useEffect(() => {
    if (!enabled || !isReady) {
      setNotificationState('none');
      return;
    }

    // Priority: update > version-ready > install > show installed status on first load
    if (hasUpdate && !isUpdating) {
      setNotificationState('update');
    } else if (isVersionReady) {
      setNotificationState('version-ready');
    } else if (
      !isInstalled &&
      canInstall &&
      !isInstalling &&
      storageService.shouldShowPWAInstall()
    ) {
      // Only show install when the browser actually supports it (beforeinstallprompt captured) and not shown this session
      setNotificationState('install');
    } else if (isInstalled && !hasShownSessionInstalledNotification) {
      // Show installed notification briefly when app is installed and we haven't shown it this session
      setNotificationState('installed');
      setHasShownSessionInstalledNotification(true);
      // Set 3-second auto-hide timer for installed notification
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => {
        setNotificationState('none');
        timerRef.current = null;
      }, 3000);
    } else {
      setNotificationState('none');
    }
  }, [
    hasUpdate,
    isUpdating,
    isVersionReady,
    canInstall,
    isInstalling,
    isInstalled,
    isReady,
    enabled,
    hasShownSessionInstalledNotification,
  ]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  // Reset installed notification flag when installation status changes
  // (Removed - no longer needed)

  // Handle update action
  const handleUpdate = () => {
    reloadForUpdate();
    setNotificationState('none');
  };

  // Handle install action
  const handleInstall = () => {
    storageService.markPWAInstallShown();
    installApp();
    // Clear timer when user manually installs
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  // Handle dismiss
  const handleDismissUpdate = () => {
    dismissUpdate();
    setNotificationState('none');
  };

  const handleDismissInstall = () => {
    storageService.markPWAInstallShown();
    setNotificationState('none');
    // Clear timer when user dismisses
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  // Handle version ready action
  const handleVersionReady = () => {
    reloadApp();
    setNotificationState('none');
  };

  // Computed visibility flags
  const showUpdate = notificationState === 'update';
  const showVersionReady = notificationState === 'version-ready';
  const showInstall = notificationState === 'install';
  const showInstalled = notificationState === 'installed';

  // Common alert styling with improved positioning and theming
  const alertSx = {
    position: 'fixed' as const,
    left: { xs: 16, sm: 24 },
    top: { xs: 16, sm: 24 },
    zIndex: 9999, // High z-index to ensure it appears above everything
    maxWidth: { xs: 'calc(100vw - 32px)', sm: 400 },
    boxShadow: theme.palette.mode === 'dark' ? 6 : 3,
    borderRadius: 3,
    backdropFilter: 'blur(8px)', // Enhanced backdrop blur for better visibility
    background:
      theme.palette.mode === 'dark'
        ? 'rgba(46, 46, 46, 0.95)'
        : 'rgba(255, 255, 255, 0.95)',
    border: `1px solid ${theme.palette.divider}`,
  };

  const slideDirection = 'down'; // Always slide down from top

  if (!enabled) return null;

  return (
    <>
      {/* App Update Available */}
      <Slide
        direction={slideDirection}
        in={showUpdate}
        mountOnEnter
        unmountOnExit
      >
        <Alert
          severity='info'
          sx={alertSx}
          icon={<UpdateIcon sx={{ fontSize: 24 }} />}
          action={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                color='inherit'
                size='small'
                onClick={handleUpdate}
                disabled={isUpdating}
                startIcon={(() => {
                  if (isUpdating)
                    return <CircularProgress size={16} color='inherit' />;
                  return <UpdateIcon />;
                })()}
                sx={{
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: 2,
                }}
              >
                {(() => {
                  if (isUpdating) return t('version_notifier.updating');
                  return t('version_notifier.reload_for_update');
                })()}
              </Button>
              <IconButton
                size='small'
                color='inherit'
                onClick={handleDismissUpdate}
                disabled={isUpdating}
                sx={{ borderRadius: 1 }}
              >
                <CloseIcon fontSize='small' />
              </IconButton>
            </Box>
          }
        >
          <AlertTitle sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
            {t('version_notifier.new_version_available')}
          </AlertTitle>
          <Typography variant='body2' sx={{ mt: 0.5, opacity: 0.9 }}>
            {t('version_notifier.new_version_description')}
          </Typography>
        </Alert>
      </Slide>

      {/* Version Ready - User Action Required */}
      <Slide
        direction={slideDirection}
        in={showVersionReady}
        mountOnEnter
        unmountOnExit
      >
        <Alert
          severity='success'
          sx={alertSx}
          icon={<UpdateIcon sx={{ fontSize: 24 }} />}
          action={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button
                color='inherit'
                size='small'
                onClick={handleVersionReady}
                startIcon={<UpdateIcon />}
                sx={{
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: 2,
                }}
              >
                {t('version_notifier.reload_now')}
              </Button>
            </Box>
          }
        >
          <AlertTitle sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
            {t('version_notifier.version_ready_title')}
          </AlertTitle>
          <Typography variant='body2' sx={{ mt: 0.5, opacity: 0.9 }}>
            {t('version_notifier.version_ready_description')}
          </Typography>
        </Alert>
      </Slide>

      {/* App Install Available - Enhanced 15 Second Toaster */}
      <Slide
        direction={slideDirection}
        in={showInstall}
        mountOnEnter
        unmountOnExit
      >
        <Card
          sx={{
            ...alertSx,
            ...((showUpdate || showVersionReady) && {
              top: { xs: 140, sm: 148 },
            }),
            background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
            color: theme.palette.primary.contrastText,
            border: `2px solid ${theme.palette.primary.main}`,
          }}
        >
          <CardContent sx={{ pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <PhoneIcon
                sx={{
                  fontSize: 32,
                  color: 'white',
                  mt: 0.5,
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
                }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant='h6'
                  sx={{
                    fontWeight: 700,
                    fontSize: '1.2rem',
                    color: 'white',
                    mb: 0.5,
                  }}
                >
                  {t('version_notifier.install_app_title')}
                </Typography>
                <Typography
                  variant='body2'
                  sx={{
                    mb: 1,
                    color: 'rgba(255, 255, 255, 0.9)',
                    lineHeight: 1.4,
                  }}
                >
                  {t('version_notifier.install_app_description')}
                </Typography>
                <Box
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    borderRadius: 2,
                    p: 1,
                    mb: 1,
                  }}
                >
                  <Typography
                    variant='caption'
                    sx={{
                      color: 'white',
                      fontWeight: 600,
                      whiteSpace: 'pre-line',
                      fontSize: '0.75rem',
                    }}
                  >
                    {t('version_notifier.install_app_benefits')}
                  </Typography>
                </Box>
              </Box>
              <IconButton
                size='small'
                onClick={handleDismissInstall}
                disabled={isInstalling}
                sx={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                  },
                }}
              >
                <CloseIcon fontSize='small' />
              </IconButton>
            </Box>
          </CardContent>
          <CardActions sx={{ pt: 0, pb: 2, px: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
              <Button
                variant='contained'
                size='small'
                onClick={handleInstall}
                disabled={isInstalling}
                startIcon={(() => {
                  if (isInstalling)
                    return <CircularProgress size={16} color='inherit' />;
                  return <InstallIcon />;
                })()}
                sx={{
                  flex: 1,
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: 2,
                  backgroundColor: 'white',
                  color: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(255, 255, 255, 0.5)',
                    color: 'rgba(0, 0, 0, 0.3)',
                  },
                }}
              >
                {(() => {
                  if (isInstalling) return t('version_notifier.installing');
                  return t('version_notifier.install_app');
                })()}
              </Button>
              <Button
                variant='outlined'
                size='small'
                onClick={handleDismissInstall}
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  borderColor: 'rgba(255, 255, 255, 0.6)',
                  color: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                {t('version_notifier.dismiss')}
              </Button>
            </Box>
          </CardActions>
        </Card>
      </Slide>

      {/* App Successfully Installed */}
      <Fade in={showInstalled} timeout={300}>
        <Alert
          severity='info'
          sx={{
            ...alertSx,
            ...((showUpdate || showVersionReady) && {
              top: { xs: 140, sm: 148 },
            }),
            ...((showUpdate || showVersionReady || showInstall) && {
              top: { xs: 280, sm: 288 },
            }),
            backgroundColor: theme.palette.primary.main,
            color: 'white',
            '& .MuiAlert-icon': {
              color: 'white',
            },
          }}
          icon={<CheckIcon sx={{ fontSize: 24 }} />}
          action={
            <IconButton
              size='small'
              onClick={() => setNotificationState('none')}
              sx={{
                borderRadius: 1,
                color: 'rgba(255, 255, 255, 0.8)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  color: 'white',
                },
              }}
            >
              <CloseIcon fontSize='small' />
            </IconButton>
          }
        >
          <AlertTitle
            sx={{ fontWeight: 700, fontSize: '1.1rem', color: 'white' }}
          >
            {t('version_notifier.app_installed_successfully')}
          </AlertTitle>
          <Typography
            variant='body2'
            sx={{ mt: 0.5, color: 'rgba(255, 255, 255, 0.9)' }}
          >
            {t('version_notifier.app_installed_description')}
          </Typography>
        </Alert>
      </Fade>
    </>
  );
}
